import { useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { Route as AdminLayoutRoute } from "./_layout";
import { CheckCircle } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { TextArea } from "@/components/base/textarea/textarea";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { Modal } from "@/components/application/modals/modal-controlled";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
// useToast nemá zatím nativní UUI ekvivalent — ponecháno na legacy barrelu (řeší samostatný teardown).
import { useToast } from "@/components/application/notifications/toast-provider";
import {
  useAdminMicrogroupInvitations,
  useApproveInvitation,
  useRejectInvitation,
} from "../../hooks/useMicrogroupQueries";
import type {
  MicrogroupInvitationRow,
} from "../../schemas/microgroup";
import { ApiError } from "../../lib/apiClient";

/**
 * UC-006 Petr microgroup approval queue (Phase 28 / Milestone 20.2).
 *
 * Data Table list `microgroup_invitation` status=`pending_admin_approval`
 * (Cesta A — admin route per ANALYZE §2.6). Approve / Reject buttons otevřou
 * modal s povinným reason min 10 znaků (audit log entry — analog R-035
 * resolution flow z Phase 14).
 *
 * Backend endpoints:
 *   - GET   /api/admin/microgroup-invitations?status=pending_admin_approval
 *   - POST  /api/admin/microgroup-invitations/{id}/approve  { reason }
 *   - POST  /api/admin/microgroup-invitations/{id}/reject   { reason }
 */

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/microgroup-requests",
  component: MicrogroupRequestsComponent,
});

type Decision = "approve" | "reject";

function MicrogroupRequestsComponent() {
  const query = useAdminMicrogroupInvitations("pending_admin_approval");
  const approve = useApproveInvitation();
  const reject = useRejectInvitation();
  const toast = useToast();

  const [openDecision, setOpenDecision] = useState<{
    invitation: MicrogroupInvitationRow;
    decision: Decision;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);

  const submitting = approve.isPending || reject.isPending;

  function startDecision(invitation: MicrogroupInvitationRow, decision: Decision) {
    setOpenDecision({ invitation, decision });
    setReason("");
    setReasonError(null);
  }

  function closeDecision() {
    setOpenDecision(null);
    setReason("");
    setReasonError(null);
  }

  function submitDecision() {
    if (!openDecision) return;
    const trimmed = reason.trim();
    if (trimmed.length < 10) {
      setReasonError("Důvod musí mít alespoň 10 znaků (audit log entry).");
      return;
    }
    const mutation = openDecision.decision === "approve" ? approve : reject;
    mutation.mutate(
      {
        invitationId: openDecision.invitation.invitation_id,
        payload: { reason: trimmed },
      },
      {
        onSuccess: () => {
          toast.push({
            variant: "success",
            message:
              openDecision.decision === "approve"
                ? "Pozvánka schválena, e-mail s odkazem odeslán."
                : "Pozvánka zamítnuta, Milan obdrží notifikaci.",
          });
          closeDecision();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.errorCode === "ALREADY_DECIDED") {
            setReasonError("Pozvánka už byla rozhodnuta jiným adminem.");
            return;
          }
          setReasonError(
            err instanceof Error ? err.message : "Rozhodnutí selhalo.",
          );
        },
      },
    );
  }

  const columns: Column<MicrogroupInvitationRow>[] = [
    {
      key: "invited",
      header: "Pozvaný",
      cell: (row) => (
        <div>
          <strong className="text-primary">{row.invited_name}</strong>
          <div className="text-xs text-tertiary">{row.invited_email}</div>
        </div>
      ),
    },
    {
      key: "requester",
      header: "Pozvání podal",
      cell: (row) => row.requester_display_name,
    },
    {
      key: "microgroup",
      header: "Mikroskupina",
      cell: (row) => row.microgroup_name,
    },
    {
      key: "relationship",
      header: "Vztah",
      cell: (row) => <Badge color="blue">{row.relationship}</Badge>,
    },
    {
      key: "created_at",
      header: "Vytvořeno",
      cell: (row) =>
        new Date(row.created_at).toLocaleString("cs-CZ", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      key: "actions",
      header: "Akce",
      cell: (row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            color="primary"
            onPress={() => startDecision(row, "approve")}
            data-testid={`microgroup-approve-${row.invitation_id}`}
          >
            Schválit
          </Button>
          <Button
            size="sm"
            color="primary-destructive"
            onPress={() => startDecision(row, "reject")}
            data-testid={`microgroup-reject-${row.invitation_id}`}
          >
            Zamítnout
          </Button>
        </div>
      ),
    },
  ];

  return (
    <section aria-labelledby="mg-requests-heading" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="mg-requests-heading" className="text-display-xs font-bold text-primary m-0">
          Žádosti o pozvánky mikroskupin
        </h2>
        <p className="text-md text-tertiary m-0">
          Schvalování členů, kteří se chtějí připojit do existující mikroskupiny.
        </p>
      </header>

      <Card>
        <CardHeader title="Čekající žádosti" />
        <CardBody>
          {query.isLoading && <LoadingIndicator label="Načítám…" />}
          {query.isError && (
            <InlineAlert variant="error" title="Načítání selhalo">
              Frontu pozvánek se nepodařilo načíst.
            </InlineAlert>
          )}
          {query.data && query.data.rows.length === 0 ? (
            <EmptyState size="sm">
              <EmptyState.Header>
                <EmptyState.FeaturedIcon color="gray" icon={CheckCircle} />
              </EmptyState.Header>
              <EmptyState.Content>
                <EmptyState.Title>Žádné čekající žádosti</EmptyState.Title>
                <EmptyState.Description>
                  Vše rozhodnuté. Nové žádosti se objeví automaticky.
                </EmptyState.Description>
              </EmptyState.Content>
            </EmptyState>
          ) : (
            query.data && (
              <DataTable
                caption="Pozvánky čekající na admin schválení"
                columns={columns}
                rows={query.data.rows}
                rowKey={(r) => r.invitation_id}
                emptyMessage="Žádné žádosti."
              />
            )
          )}
        </CardBody>
      </Card>

      {openDecision && (
        <Modal
          open={openDecision !== null}
          title={
            openDecision.decision === "approve"
              ? `Schválit pozvánku — ${openDecision.invitation.invited_name}`
              : `Zamítnout pozvánku — ${openDecision.invitation.invited_name}`
          }
          onClose={closeDecision}
          footer={
            <>
              <Button color="secondary" onPress={closeDecision} isDisabled={submitting}>
                Zrušit
              </Button>
              <Button
                color={
                  openDecision.decision === "approve" ? "primary" : "primary-destructive"
                }
                onPress={submitDecision}
                isDisabled={submitting}
                isLoading={submitting}
                data-testid="microgroup-decision-submit"
              >
                {openDecision.decision === "approve" ? "Schválit" : "Zamítnout"}
              </Button>
            </>
          }
        >
          <p>
            {openDecision.decision === "approve"
              ? "Po schválení obdrží pozvaný e-mail s aktivačním odkazem (platnost 14 dní)."
              : "Zamítnutí ukončí pozvánku — Milan obdrží notifikaci s důvodem."}
          </p>
          <TextArea
            label="Důvod rozhodnutí (min 10 znaků)"
            isRequired
            isInvalid={!!reasonError}
            hint={reasonError ?? undefined}
            rows={4}
            value={reason}
            onChange={setReason}
            placeholder="Např. ověřil jsem identitu pozvaného…"
            data-testid="microgroup-decision-reason"
          />
        </Modal>
      )}
    </section>
  );
}
